import { Subject } from 'rxjs';
import { IReportCandidateLeadingInDistrict } from 'src/app/interfaces/IReportCandidateLeadingInDistrict';
import { IReportSeatsWon } from 'src/app/interfaces/IReportSeatsWon';
import { ElectionService } from 'src/app/services/election.service';
import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { DataSourcesEnum } from 'src/app/enums/DataSourcesEnum';
import { ElectionStatusEnum } from 'src/app/enums/ElectionStatusEnum';
import { ISelectPage } from 'src/app/interfaces/ISelectPage';
import { PageTypeEnum } from 'src/app/enums/PageTypeEnum';
import * as _ from 'lodash';
import { ViewEncapsulation } from '@angular/core';

@Component({
  selector: 'app-district-successful-candidate',
  templateUrl: './district-successful-candidate.component.html',
  styleUrls: ['./district-successful-candidate.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DistrictSuccessfulCandidateComponent implements AfterViewInit, OnDestroy {

  private componentDestroyed$: Subject<boolean> = new Subject<boolean>();
  public seatsWons: IReportSeatsWon[] = [];
  public isFinal = false;
  public isPreCount = false;
  public isCounting = false;
  public candidateName = '';
  public candidateAffiliation = '';
  public candidates: IReportCandidateLeadingInDistrict[] = [];

  @Input() districtName: any;

  constructor(public electionService: ElectionService) {
    this.electionService.dataLoaded$.pipe(takeUntil(this.componentDestroyed$)).subscribe(dataLoaded => {
      if (this.isDataLoaded(dataLoaded.electionDate)) {
        this.loadChart(this.electionService.selectedPage);
      }
    });
    this.electionService.pageChanged$.pipe(takeUntil(this.componentDestroyed$)).subscribe(page => {
      this.loadChart(page);
    });
  }

  ngAfterViewInit(): void {
    if (this.isDataLoaded(this.electionService.selectedPage.electionDateString ?? '')) {
      this.loadChart(this.electionService.selectedPage);
    }
  }

  private isDataLoaded(electionDateString: string): boolean {
    const election = this.electionService.electionMap.get(electionDateString);
    if (election) {
      return election.dataSourceTrigger([
        DataSourcesEnum.HAChange,
        DataSourcesEnum.HAStatic
      ]);
    }
    return false;
  }

  private loadChart(page: ISelectPage): void {
    const election = this.electionService.electionMap.get(page.electionDateString ?? '');
    if (!election) {
      console.error('Election data not found for:', page);
      return;
    }
  
    // Clear all flags before setting them based on the current election status
    this.isFinal = false;
    this.isCounting = false;
    this.isPreCount = false;
  
    if (page.pageType === PageTypeEnum.CountHADistrict) {
      this.candidates = election.electionCount?.districts.filter(x => x.leadingCandidate !== null).map(district => {
        return {
          district: district.districtName,
          candidateName: `${district.leadingCandidate?.candidateName ?? ''} ${district.leadingCandidate?.wasPreviousMember ? ' (M)' : ''}`,
          wasPreviousMember: district.leadingCandidate?.wasPreviousMember ?? false,
          partyName: `${district.leadingCandidate?.partyName ?? ''} (${district.leadingCandidate?.partyId ?? ''})`,
          colourCode: district.leadingCandidate?.colourCode ?? '',
          distributionVotePercent: district.leadingCandidate?.twoCPVotePercent ?? 0,
          divWid: '',
          divStyle: `width: ${Math.round(district.leadingCandidate?.twoCPVotePercent ?? 0)}px; fill: ${district.leadingCandidate?.colourCode}; stroke-width: 3; stroke: ${district.leadingCandidate?.colourCode}`
        };
      }) ?? [];
    }

    const status: ElectionStatusEnum = election?.haChange?.electionStatus!;

    // set election status flags based on the current election status
    switch (status) {
      case ElectionStatusEnum.final: {
        this.isFinal = true;
        break;
      }
      case (ElectionStatusEnum.declaration): {
        this.isCounting = true;
        break;
      }
      case (ElectionStatusEnum.primary): {
        this.isCounting = true;
        break;
      }
      case (ElectionStatusEnum.recheck): {
        this.isCounting = true;
        break;
      }
      default: {
        this.isPreCount = true;
        break;
      }
    }

    // Use the district from the current page so we show the leading candidate for the selected district
    const selectedDistrictName = page.pageType === PageTypeEnum.CountHADistrict ? page.countDistrict : this.districtName;
    this.fillCandidateInfo(selectedDistrictName);
  }

  private fillCandidateInfo(selectedDistrictName: string | null): void {
    const match = this.candidates.find(c => c.district === selectedDistrictName);
    if (!match) {
      this.candidateName = '';
      this.candidateAffiliation = '';
      return;
    }
    const parts = match.candidateName.split(', ');
    const candidateNameFormatted = parts.length >= 2
      ? parts[1] + ' ' + _.capitalize(parts[0])
      : match.candidateName.trim();
    let candidateName = candidateNameFormatted;
    if (candidateName.includes("'")) {
      const nameParts = candidateName.split("'");
      if (nameParts.length === 2) {
        candidateName = nameParts[0] + "'" + nameParts[1].charAt(0).toUpperCase() + nameParts[1].slice(1);
      }
    }
    this.candidateName = candidateName;
    this.candidateAffiliation = match.partyName ?? '';
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

}
